import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Mail } from "lucide-react";

const fmt = (n: number) => n.toLocaleString("pt-BR");

export function EmailTab() {
  const { data, isLoading } = trpc.superAdmin.sendgridUsage.useQuery();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" /> Carregando…
      </div>
    );
  }

  const gs = data?.globalStats;
  const deliveryRate = gs && gs.requests > 0 ? ((gs.delivered / gs.requests) * 100).toFixed(1) : "—";
  const openRate = gs && gs.delivered > 0 ? ((gs.opens / gs.delivered) * 100).toFixed(1) : "—";

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
          <Mail className="w-5 h-5 text-amber-500" />
          E-mail / SendGrid
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Consumo da conta SendGrid centralizada neste mês.</p>
      </div>

      {gs ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground uppercase tracking-wide">Enviados</p><p className="text-2xl font-bold mt-1">{fmt(gs.requests)}</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground uppercase tracking-wide">Entregues</p><p className="text-2xl font-bold mt-1">{fmt(gs.delivered)}</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground uppercase tracking-wide">Taxa Entrega</p><p className="text-2xl font-bold mt-1">{deliveryRate}%</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground uppercase tracking-wide">Aberturas</p><p className="text-2xl font-bold mt-1">{fmt(gs.opens)}</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground uppercase tracking-wide">Taxa Abertura</p><p className="text-2xl font-bold mt-1">{openRate}%</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground uppercase tracking-wide">Bounces</p><p className="text-2xl font-bold mt-1 text-red-600">{fmt(gs.bounces)}</p></CardContent></Card>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4">
          Não foi possível obter estatísticas da API do SendGrid. Verifique se <code>SENDGRID_API_KEY</code> está configurada.
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Consumo por Empresa (mês atual)</CardTitle></CardHeader>
        <CardContent className="p-0">
          {!data?.perTenantUsage.length ? (
            <p className="text-sm text-muted-foreground p-6">Nenhum e-mail enviado neste mês.</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead className="text-right">Enviados</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.perTenantUsage.map((t: any) => (
                  <TableRow key={t.tenantId}>
                    <TableCell className="font-medium">{t.tenantName}</TableCell>
                    <TableCell className="text-right">{fmt(t.emailsSent)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Domínios Autenticados</CardTitle></CardHeader>
        <CardContent className="p-0">
          {!data?.domains.length ? (
            <p className="text-sm text-muted-foreground p-6">Nenhum domínio cadastrado.</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Domínio</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Verificado em</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.domains.map((d: any) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-mono text-xs">{d.domain}</TableCell>
                    <TableCell>{d.tenantName}</TableCell>
                    <TableCell>
                      <Badge variant={d.status === "verified" ? "default" : d.status === "failed" ? "destructive" : "secondary"} className="text-[10px]">
                        {d.status === "verified" ? "Verificado" : d.status === "failed" ? "Falhou" : "Pendente"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {d.verifiedAt ? new Date(d.verifiedAt).toLocaleDateString("pt-BR") : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
